
import { Component, Vue } from "vue-property-decorator";
import httpClient from "../../store/httpClient";
import moment from "moment";
import DatePicker from "@/components/DatePicker.vue";
import { CreditNoteDto } from "@/dtos/creditNoteDto";

@Component({
	components: {
		DatePicker,
	},
})
export default class CreditNotes extends Vue {
	private loading: boolean = false;
	private creditNotes: CreditNoteDto[] = [];
	private searchTerm: string = "";
	private activeTab: number = 0;
	private startDate: string = moment().format("YYYY-01-01 00:00:00");
	private endDate: string = moment().format("YYYY-12-31 23:59:59");
	private deleteDialogOpen: boolean = false;
	private deleting: boolean = false;
	private deleteReason: string = "";
	private creditNoteIdToDelete: number = 0;

	private openHeaders = [
		{ text: "Datum", value: "createdAt", align: "left" },
		{ text: "KndNr", value: "customerId", align: "left" },
		{ text: "Kunde", value: "customerName", align: "left" },
		{ text: "Betrag", value: "amount", align: "right" },
		{ text: "Grund", value: "reason", align: "left" },
		{ text: "Auftrag", value: "createdFromCustomerOrderId", align: "left" },
		{ text: "", value: "actions", align: "right", sortable: false },
	];

	private redeemedHeaders = [
		{ text: "Datum", value: "createdAt", align: "left" },
		{ text: "KndNr", value: "customerId", align: "left" },
		{ text: "Kunde", value: "customerName", align: "left" },
		{ text: "Betrag", value: "amount", align: "right" },
		{ text: "Grund", value: "reason", align: "left" },
		{ text: "Auftrag", value: "createdFromCustomerOrderId", align: "left" },
		{ text: "Rechnung", value: "invoiceId", align: "left" },
		{ text: "", value: "actions", align: "right", sortable: false },
	];

	private deletedHeaders = [
		{ text: "Datum", value: "createdAt", align: "left" },
		{ text: "KndNr", value: "customerId", align: "left" },
		{ text: "Kunde", value: "customerName", align: "left" },
		{ text: "Betrag", value: "amount", align: "right" },
		{ text: "Grund", value: "reason", align: "left" },
		{ text: "Auftrag", value: "createdFromCustomerOrderId", align: "left" },
		{ text: "Rechnung", value: "invoiceId", align: "left" },
		{ text: "Gelöscht am", value: "deletedAt", align: "left" },
		{ text: "Löschgrund", value: "deleteReason", align: "left" },
		{ text: "", value: "actions", align: "right", sortable: false },
	];

	private mounted() {
		this.loadCreditNotes();
	}

	private get openCreditNotes() {
		return this.creditNotes.filter((c) => c.deletedAt == null && c.invoiceId == null);
	}

	private get redeemedCreditNotes() {
		return this.creditNotes.filter((c) => c.deletedAt == null && c.invoiceId != null);
	}

	private get deletedCreditNotes() {
		return this.creditNotes.filter((c) => c.deletedAt != null);
	}

	private async loadCreditNotes() {
		try {
			this.loading = true;
			const response = await httpClient().get(
				`CreditNote/GetCreditNotes?from=${this.startDate}&to=${this.endDate}` +
					`&searchTerm=${encodeURIComponent(this.searchTerm || "")}`
			);
			this.creditNotes = response.data;
		} catch {
			this.$store.commit(
				"setSnackbarErrorText",
				"Gutschriften konnten nicht geladen werden."
			);
		} finally {
			this.loading = false;
		}
	}

	private setStartDate(date: Date) {
		this.startDate = moment(date).format("YYYY-MM-DD 00:00:00");
	}

	private setEndDate(date: Date) {
		this.endDate = moment(date).format("YYYY-MM-DD 23:59:59");
	}

	private goToCustomer(customerId: number) {
		this.$router.push({
			name: "customerDetail",
			params: { customerId: customerId.toString() },
		});
	}

	private openDeleteDialog(creditNoteId: number) {
		this.creditNoteIdToDelete = creditNoteId;
		this.deleteReason = "";
		this.deleteDialogOpen = true;
	}

	private async confirmDelete() {
		try {
			this.deleting = true;
			await httpClient().post(
				`CreditNote/DeleteCreditNote?id=${this.creditNoteIdToDelete}` +
					`&reason=${encodeURIComponent(this.deleteReason)}`
			);
			this.$store.commit("setSnackbarText", "Gutschrift wurde gelöscht.");
			this.deleteDialogOpen = false;
			await this.loadCreditNotes();
		} catch {
			this.$store.commit("setSnackbarErrorText", "Gutschrift konnte nicht gelöscht werden.");
		} finally {
			this.deleting = false;
		}
	}

	private async downloadPdf(creditNoteId: number) {
		try {
			const response = await httpClient().get(
				`CreditNote/GetCreditNotePdf?creditNoteId=${creditNoteId}`,
				{ responseType: "blob" }
			);
			const url = window.URL.createObjectURL(new Blob([response.data]));
			const link = document.createElement("a");
			link.href = url;
			link.setAttribute("download", `Gutschrift_${creditNoteId}.pdf`);
			link.click();
			window.URL.revokeObjectURL(url);
		} catch {
			this.$store.commit(
				"setSnackbarErrorText",
				"Gutschrift-PDF konnte nicht geladen werden."
			);
		}
	}
}
